import { BreakthroughCycle, GrowthPhase, Task, Tip } from "@/types/playbook";

// Welcome Screen Content - adapted from the old firstPageIntroduction
export const welcomeContent: GrowthPhase = {
  id: "welcome-screen",
  number: 0,
  title: "Welcome to LaunchGuide",
  introduction: `This is your Growth Playbook: A step by step guide to get you from zero to $10k MRR.
Growing a SaaS product from zero isn't about doing everything. It's about doing the right things at the right time. In the early stages of your launch, you don't need branding exercises or slow-burn SEO.
You need users… now. 
That's why we start with high-impact, fast-acquisition strategies like:
Commenting in communities
Direct outreach (DMs, cold emails, warm intros)
Social circle leverage
Quick paid ad tests (if you're ready)
These are scrappy, sales-style tactics designed to get traction fast. We want feedback, users, and momentum, not perfection. But we don't stop there. As your product gains its first users and you start learning what works, the strategy shifts.
In the later stages of the course, we focus on compounding growth activities like:
SEO and blog content
Backlink building
Growing a social presence
Building partnerships and user-generated content
Creating scalable lead magnets and email flows
These are slower to show results, but they keep working in the background long after you've built them. Each phase in LaunchGuide is designed to match where your product is in its growth journey.
So don't worry about doing everything at once. Just follow the plan, focus on execution, and let the momentum build.
Ready to get started? Let's dive into the first breakthrough cycle.`,
  keyTargets: [],
  tasks: []
};

// All Breakthrough Cycles
export const breakthroughCycles: BreakthroughCycle[] = [
  // Cycle 0: Getting The Engine Started
  {
    id: "cycle-getting-engine-started",
    title: "Breakthrough Cycle 0: Getting The Engine Started",
    goals: "Kick-start a flood of real users in days, not months. Pair proven Direct Message sequences with magnetic comments and proof posts that land your product where prospects already look. Finish the cycle with live users, actionable feedback, and a surge of momentum that proves LaunchGuide is your growth rocket.",
    targets: [
      "Credible Presence: Establish a personal profile that instantly signals trust and relevance.",
      "Curiosity Spark: Trigger an initial wave of intrigue that draws prospects to you.",
      "First Users: Convert that curiosity into a first batch of real users or beta sign‑ups.",
      "Early Proof: Demonstrate that your solution solves a real problem and deserves attention.",
      "Familiar Footprint: Show up where future users congregate so your name feels familiar when outreach begins.",
      "Growth Foundation: Put in place sign‑up points, proof assets, and authority signals that will compound in later cycles."
    ],
    phases: [
      // Phase 1: Personal profile set-up
      {
        id: "phase-personal-profile-setup",
        number: 1,
        title: "Personal profile set-up",
        introduction: "You'll be commenting and sending direct messages (dm's) soon, but people will check your profile before replying. If it looks empty, it kills trust. Setting up your personal account now makes sure you look real, credible, and relevant before outreach begins. It also lays the groundwork for your first posts in the next phase.",
        keyTargets: [
          "Core personal profile live on at least one platform that fits your audience",
          "Profile bio created or updated",
          "Profile image and link to landing page added",
          "Optional business handle reserved on the same platform"
        ],
        tasks: [
          {
            id: "task-identify-platform",
            description: "Task 1 - Identify the main platform where you can find your target audience",
            innerContent: `Every SaaS product ultimately falls into one of two camps—business-focused (B2B) or consumer-focused (B2C), and knowing which side you're on is the first step in zeroing in on where to find your ideal users.

Below is a quick guide that maps each segment to the high-leverage channels where those audiences already hang out. Think of it as your outreach compass: pick your quadrant, then double down on the platforms where your prospects live, breathe, and debate every day.

B2B SaaS
- Serves business users (e.g. marketing automation, CRM, analytics, project tools)
- Where to find them:
  • LinkedIn (industry groups, Sales Navigator searches)
  • X.com (hashtags like #B2BSaaS, #GrowthHacking)
  • Slack communities (e.g. Online Geniuses, Sales Hacker, DataTalks.Club)
  • Reddit (r/sales, r/saaS, r/analytics)

B2C SaaS
- Serves consumers (e.g. fitness, personal finance, photo/video editing, habit-tracking)
- Where to find them:
  • Instagram & TikTok (influencers, niche hashtags)
  • Facebook groups (e.g. "Healthy Habits," "Budgeting Tips")
  • YouTube (how-to videos, product reviews)
  • Reddit (relevant subreddits like r/fitnessapps, r/personalfinance)
  • App stores & review sites (top charts, featured collections)`
          },
          {
            id: "task-setup-account",
            description: "Task 2 - Create account or log in",
            innerContent: `Username
If you want to always make it clear what service you're about, add it to your username.

Example
Peter Parker | LaunchGuide.io
Sarah White | Ecommerce Marketing
Jerry Salami - Leads On Autopilot`
          },
          {
            id: "task-update-bio",
            description: "Task 3 - Describe what you've built and add anything relevant to your bio",
            innerContent: `How to write a killer bio

Even though it's 'your' bio, always remember your target audience thinks "what's in it for me?".

That means you want to communicate the benefits of following you and why they should be listening to you. Insert achievements to communicate you're an authority on the topic.

Template #1
Founder @yourSaaS. Get [desired outcome] 
Try it/Sign Up: www.yoursaas.com

Example
Founder @Appio
Build apps through conversation, not clicks
Try it: www.appio.ai

Template #2
Founder of the world's [used/popular/played/read/etc.] @yourSaas
[achievement]
Sharing the latest [types of post you make]

Example
Founder of the world's fastest growing AI cold email platform @yourtool
Sent over 10 million cold emails in 2023
Sharing the latest cold email strategies

Template #3
I help [target audience] [desired outcome] [timeframe] with [method]
[achievement]

Example
I help founders get more users 10x faster with little-known marketing strategies
Invested over $15,000,000 in Meta ads

Template #4
@yourtool [what it does] by [easy of use]

Example
@yourtool Steal any website UI by copy/pasting it. Converted into code instantly`
          },
          {
            id: "task-add-website",
            description: "Task 4 - Add your landing page or wait-list link in the website field"
          },
          {
            id: "task-check-old-content",
            description: "Task 5 - If you already have an account, spend 10 min checking and removing old posts that don't support your business"
          },
          {
            id: "task-join-communities",
            description: "Task 6 - Join 3-5 relevant communities on the platform where you can find your audience"
          },
          {
            id: "task-follow-influencers",
            description: "Task 7 - Follow 20 influencers who your target audience follows"
          }
        ],
        tips: [
          {
            id: "tip-platform-pick",
            content: "Audience-aware platform pick:\n- B2B → LinkedIn personal profile, optional business page\n- Dev tools → GitHub profile, optionally Discord handle in bio\n- Creator tools → X.com or Instagram, optional TikTok handle\n- Personal network → WhatsApp business profile, Telegram, IG stories"
          }
        ]
      },
      // Phase 2: First public proof post
      {
        id: "phase-first-public-proof-post",
        number: 2,
        title: "First public proof post",
        introduction: "Public posts create instant social proof and let qualified prospects self-select into your beta. By using the generator you avoid blank-page paralysis and you don't need to worry about what to post.",
        keyTargets: [
          "Two posts or threads published on your profile on your chosen primary platform",
          "Each post ends with a soft CTA (e.g., 'DM \"beta\" if this resonates')."
        ],
        tasks: [
          {
            id: "task-generate-topics",
            description: "Task 1 - Go to Generate Content → Click the \"Generate Topics\" button to generate 30 keyword optimised Content Topics"
          },
          {
            id: "task-generate-posts",
            description: "Task 2 - Generate 2 posts by selecting a topic → Generate → select your platform (LinkedIn, X thread, etc.)"
          },
          {
            id: "task-edit-posts",
            description: "Task 3 - Edit for tone and add a Call To Action (CTA)",
            innerContent: `How to write a Call To Action that makes them click

The CTA (Call to Action) is one of the most important elements in marketing, yet often overlooked.

People rarely take action on their own. A clear CTA removes friction, reduces hesitation, and tells them exactly what to do next.

Without it, even the best message falls flat.

Many skip the CTA out of fear of being too salesy, or they assume the value will speak for itself. But in reality, audiences need guidance - especially in a world full of distractions.

If you believe in your product, guiding people with a strong CTA isn't pushy, it's helpful.

Important note: When you're sharing educational content, you can leave out the CTA. The educational content creates trust and authority, the promotional content will make the conversion.

Examples

1. Lead Generation
👉 "Want the full breakdown? Download the free guide here"

2. Product Trial
👉 "Try it free for 7 days - no credit card needed"

3. Demo Request
👉 "Book a quick demo - I'll show you how it works in 15 minutes"

4. Beta Access
👉 "We've got 3 beta spots left. Claim yours here"

5. Direct DM (social post angle)
👉 "Curious? DM me 'access' and I'll send you the details"`
          },
          {
            id: "task-publish-posts",
            description: "Task 4 - Publish both posts"
          }
        ]
      },
      // Phase 3: Community Announcement Posts
      {
        id: "phase-community-announcement-posts",
        number: 3,
        title: "Community Announcement Posts",
        introduction: "Your ideal users already gather in niche communities and groups, openly discussing the exact problems your product solves. A sharp announcement here short-cuts cold outreach, turning warm curiosity into early traction and delivering public proof that the market wants what you're building.",
        keyTargets: [
          "Five product announcement posts",
          "1 Profile post"
        ],
        tasks: [
          {
            id: "task-choose-communities",
            description: "Task 1 - Choose five communities where you will make your announcement post"
          },
          {
            id: "task-generate-content",
            description: "Task 2 - Craft your announcement post using the Ultimate Product Announcement Formula",
            innerContent: `Beta launches and positioning for feedback

You've just launched. Your product isn't perfect yet, and that's your advantage.

Invite people in, not as buyers, but as early contributors. Position the message around "help us shape this", and give them something genuinely useful in return.

This does 3 things at once

Lowers friction for people to try something new
Gets you real users, not empty likes
Converts feedback-givers into future paying users, reviews, and social proof



Use the beta feedback positioning with an honest tone

"This isn't perfect, but it's already helping people with [X]. If you're dealing with that, try it. I'd love your feedback."



Sweeten the Invite with a Beta-Only Offer

People love being early, but they love being rewarded for it even more.
If someone's willing to test a product that's still evolving and take time to give you feedback, make it worth their while.

This doesn't have to be complicated. Just add a clear, generous offer to tip them over the edge.


Here are a few powerful examples you can use

"First 50 beta users get 3 months of full access free"
"Give us feedback and get 6 months access at 80% off"
"First 100 users get double the limits of regular plans, forever"

The goal is to reward risk-takers and create a sense of exclusivity. Add a limit to create scarcity and motivate them to take action now. When people feel like insiders, they stick around longer, and tell others.


Ultimate Product Announcement Formula

Hook
Product description from the Marketing Vault
Offer
Call To Action (CTA)


Hooks


If you're having [Problem] then
If you're frustrated with
Stop doing [X] instead, do this
I bet you didn't know
Here's the ULTIMATE guide for [X]
This might be controversial, but
It should be illegal how good…
If you use [X] you should check out [Y]
All your [subject] problems can be solved using [Y]
If you want to achieve [X] Listen up!
Feeling overwhelmed with [X]? Read this
This is your sign to finally
Before you do [X], please do [Y]
Read this if you hate



Example

Feeling overwhelmed with marketing your SaaS? Read this.

LaunchGuide.io is your AI-powered marketing ally, designed for technical founders and product teams. Automate audience analysis, content creation, and strategy execution, freeing up time for core development while ensuring consistent, high-quality marketing across platforms.

This isn't perfect, but it's already helping people with getting users. If you're dealing with that, try it. I'd love your feedback.

The first 50 beta users get 3 months of full access for free after the official launch. We have 26 spots left.

Try it for free now at www.launchguide.io`
          },
          {
            id: "task-add-media",
            description: "Task 3 - Add a product image or video to the post"
          },
          {
            id: "task-publish-post",
            description: "Task 4 - Follow community rules, add a strong CTA to join your beta or trial, publish the post"
          },
          {
            id: "task-post-to-profile",
            description: "Task 5 - Post it on your personal profile and pin it if possible",
            innerContent: `How to think about your profile page

See your profile as a mini landing page where you display authority on your subject, teach and sell your product.

Post product updates, share thoughts if they are useful to others, repost interesting posts, offer your services.

You can be relatable as a person, but do not treat it as your diary.`
          },
          {
            id: "task-reply-to-comments",
            description: "Task 6 - Reply within 2h for the first 24h, then daily for the next 3 days"
          },
          {
            id: "task-dm-commenters",
            description: "Task 7 - Direct Message (DM) high-intent commenters",
            innerContent: `How to handle high-intent commenters

High-intent means someone is not just interested, they're ready to act. These are the people most likely to convert, and your DMs should treat them differently from low-effort commenters.

Look for comments that signal urgency, buying behavior, or clear relevance to what you offer.


High-Intent Triggers
Problem-aware + searching: "This is exactly what I'm struggling with"
Direct interest: "How can I get access?", "Is there a demo?"
Comparison-seeking: "How does this stack up against [X]?"
Use case matching: "Would this work for [my product/audience]?"
Action requests: "Can you DM me?", "I just signed up, now what?"

Ignore vague praise like "cool idea". That's low intent.


How to Handle High-Intent in DMs
Your goal: qualify them fast, create momentum, and give them a clear next step.

Acknowledge and match their comment
"Saw your comment, sounds like you're looking for something to help with [problem they mentioned]?"

2. Ask 1 frictionless, qualifying question
"What are you currently doing to solve it?"

3. Confirm they're a fit, then pitch with CTA
"Got it. LaunchGuide actually solves exactly that. Want me to set you up with a free account so you can test it today?"

Don't let the conversation get derailed by talking about something else. Of course conversations are dynamic, but guide the conversation towards getting the prospect to try your product.


Social Calibration Tip
If they drop any relevant context (e.g. "just launched"), reflect it back in your DM. It shows you listened, increases trust, and shortens the sales cycle.`
          },
          {
            id: "task-check-threads",
            description: "Task 8 - Check the threads daily for a week, add helpful follow-ups, and log any new pain points or feature requests"
          }
        ],
        tips: [
          {
            id: "tip-community-etiquette",
            content: "Community Etiquette:\n- Reddit – Value first; add link in a follow-up comment.\n- Slack/Discord – Post in #wins or #tools type of channels, avoid @channel blasts.\n- Facebook Groups – Check posting rules and tag admin if needed."
          }
        ]
      },
      // Phase 4: Helpful Comment URL Drops
      {
        id: "phase-helpful-comment-url-drops",
        number: 4,
        title: "Helpful Comment URL Drops",
        introduction: "Publicly replying to \"I-have-this-problem\" type comments is a low-pressure way to show authority, help real people, and invite them in. No cold DMs, yet. It warms you up to outreach, proves demand in the open, and invites them to explore your solution.",
        keyTargets: [
          "Engage on 5 influencer posts where your target users congregate",
          "20 helpful reply-comments posted"
        ],
        tasks: [
          {
            id: "task-find-influencers",
            description: "Task 1 - Search your primary platform for posts from influencers that attracted many comments of your ideal target audience",
            innerContent: `Finding the right posts

Your goal here is to find where your audience is already engaging, so you can reverse-engineer what triggers interest, and use it to guide your outreach, positioning, or content.
 
What to Look For
Focus on posts from influencers in your niche that:

Got lots of comments (50+ is ideal)
Attracted your exact audience
Sparked problems, questions, or buying signals in the replies


Green Flags in Comments
Look for people saying things like:

"I've been trying to figure this out…"
"What tools would you recommend for X?"
"This is exactly what I'm dealing with"
"Does anyone have a system for [problem]?"

These are high-intent conversations waiting to be tapped. Save the post. Screenshot great comments. These are gold for:

Crafting your offer
Starting warm DMs
Creating viral, validated content`
          },
          {
            id: "task-shortlist-posts",
            description: "Task 2 - Short-list 5 posts with lots of comments"
          },
          {
            id: "task-reply-to-comments",
            description: "Task 3 - Reply to at least 20 relevant comments across the 5 posts",
            innerContent: `When you find someone in the comments clearly struggling with a problem you solve, don't sell hard. Be direct, helpful, and low-friction.

Your Comment Structure
Acknowledge their problem
Hint at the solution (keep it simple)
Offer access (without pressure)


Plug-and-Play Comment Template
"Hey, sounds like you're dealing with [insert their problem in 3-5 words].I've actually built a tool to solve exactly that in 3 simple steps.You can try it here if you're curious: [your URL]"


Beta & Feedback Angle
"I built something to solve exactly that, it's already helping some people with similar problems. If you're open for it, you can try it out. Would love your feedback: [URL]"

"I'm working on a tool that fixes exactly this. Still early, but if you're open to trying it, here's the beta: [URL]"

"I've built something around this problem, still in beta and shaping it with early users. You can try it here: [URL]"


Bonus Tips
Be human: Match the tone of the thread. If it's casual, don't be robotic.
Be fast: The sooner you comment, the more people will see it.
Engage others: If multiple people have the same problem, reply to them too.


Important: Don't Spam, Play It Smart

You're not trying to plaster your link everywhere, you're trying to start real conversations that turn into users.

Focus on:

Top Comments First
The comments near the top of a post get the most eyeballs.
Prioritize replying to highly upvoted or early comments, that's where visibility and clicks come from.

Spread It Out
Don't drop the same comment on 10 replies in a row.
Mix up your wording, space them out over time, and only reply when it's actually relevant.
If you go too fast or look robotic, you'll get ignored or even flagged.

Avoid Always Being Link-First
Sometimes, leave the link out and just say:
"If you're open to it, I can send over the tool I made for this, helped a few others here too." Then drop the link if they reply.`
          },
          {
            id: "task-monitor-notifications",
            description: "Task 4 - Monitor notifications for questions; answer within a few hours to keep visibility high"
          },
          {
            id: "task-track-signups",
            description: "Task 5 - Track sign-ups from each post in a simple sheet to spot top-performing niches"
          }
        ]
      },
      // Phase 5: Comment and connect lite
      {
        id: "phase-comment-and-connect-lite",
        number: 5,
        title: "Comment and connect lite",
        introduction: "You've prepared your profile, posted publicly, interacted by commenting. The next step is directly reaching out to your target audience by using proven to work Direct Message (DM) sequences. Try not to overthink it and reach out. Most people won't reply, and that's normal. Your win is sending quality outreach.",
        keyTargets: [
          "10 contextual comments posted on your audience's primary platform",
          "10 DMs sent using a comment-based opener",
          "≥ 2 warm replies (meaningful response, not emoji)",
          "≥ 1 beta, trial, or demo sign-up secured"
        ],
        tasks: [
          {
            id: "task-find-relevant-comments",
            description: "Task 1 - Find posts your audience follows, look for 10 relevant comments"
          },
          {
            id: "task-like-comment",
            description: "Task 2 - Like and/or comment on your target's reply",
            innerContent: `When you find someone in the comments clearly struggling with a problem you solve. Be direct, helpful, and low-friction.

Your Comment Structure
Acknowledge their problem
Hint at the solution (keep it simple)


Approach
'Like' their comment and send them a Direct Message
Comment and send them a Direct Message


Plug-and-Play Comment Template
"Hey, sounds like you're dealing with [insert their problem in 3-5 words]. I've actually built a tool to solve exactly that in 3 simple steps"

"Hey, seems you're struggling with [insert their problem]. I might have a solution for you if you're open to suggestions"


Since our goal is to practice Direct Messaging, we are not adding our offer or link. In the future, you can mix your approach based on what you see is giving you the best results.

Let's move on to the messages in the next task.`
          },
          {
            id: "task-follow-targets",
            description: "Task 3 - Follow or Friend request the target profiles"
          },
          {
            id: "task-send-dms",
            description: "Task 4 - Send a total of 10 contextual DMs",
            innerContent: `Now on to the Direct Messages. This might be uncomfortable territory, but it's a crucial skill to learn. You've commented or liked the comment.

It's good to experiment, but here are templates of what to say:


Following up with a Direct Message

Template #1 (sent in 2 separate messages)
"Hey saw your comment about [insert a part of their comment] on [name of influencer] post about [describe the topic of the post]"

"If you're struggling with [problem], I might have a solution for you if you're open to suggestions"


If you're not 100% sure if they have the problem you solve, here's how you find out

Template #2.1 (sent in 2 separate messages)
"Hey saw your comment about [insert a part of their comment] on [name of influencer] post about [describe the topic of the post]"

"I'm curious about something!"

After they answer, follow up with
"Are you struggling with [insert their problem]? If so, I might have a solution for you if you're open to suggestions"


From there go into explaining your solution, your (beta) offer and answer any questions they might have. Keep it natural, don't let the conversation derail, try to onboard them immediately.`
          },
          {
            id: "task-track-replies",
            description: "Task 5 - Track your warm replies in a Google Sheet"
          },
          {
            id: "task-add-to-followup",
            description: "Task 6 - Add all warm leads to a follow-up list"
          },
          {
            id: "task-convert-users",
            description: "Task 7 - Convert at least 1-2 users through this flow"
          }
        ]
      },
      // Phase 6: Gather feedback & improve stickiness
      {
        id: "phase-gather-feedback",
        number: 6,
        title: "Gather feedback & improve stickiness",
        introduction: "You've built core functionality and seen initial usage, but before scaling or adding more features, you must validate your assumptions. Direct feedback uncovers hidden friction, pinpoints what truly delights users, and guides your roadmap with data rather than intuition.",
        keyTargets: [
          "Run the appropriate feedback frameworks with as many users as possible"
        ],
        tasks: [
          {
            id: "task-churn-feedback",
            description: "Feedback Framework 1 - Uncover Churn Causes with the 10X Feedback Loop",
            innerContent: `Listening to what users say is valuable, but watching what they actually do uncovers the hidden blockers that surveys miss. This approach, championed by product teams at Airbnb, Shopify and Spotify, uses session-replay tools (Hotjar, PostHog, Heap) to pinpoint exactly where users drop off.

By reaching out directly to those who abandoned a key action, you get laser-focused insights into real UX friction. This lets you fix the root cause of churn, not just its symptom, and shows users you're committed to removing their pain points.


Purpose
Surface UX friction by talking to users who dropped off


Your Steps
Identify 5 users who started a key action (sign-up, onboarding flow, core feature) but didn't complete it. Use tools like Hotjar, PostHog, or Heap.

Send each a DM using this script:
Hey [Name], noticed you tried out [your app]. Can I ask a few short questions about your experience?
What is the problem you're trying to solve with [your product]?
What would've made you come back tomorrow?
Do you use a different platform instead? If yes, tell us more about what you value about this platform.

Log replies and note recurring issues.`
          },
          {
            id: "task-kano-model",
            description: "Feedback Framework 2 - The Kano Model - Evaluate Features",
            innerContent: `Classify product features into Must-Have, Performance, or Delighter categories by probing how users feel when a feature is present versus absent. This tells you which features you must build, which improve satisfaction linearly, and which create unexpected delight.


Step 1: Select Features to Test
• Choose 5–10 key features or capabilities you want to evaluate.
• Write each feature as a clear, concise phrase (e.g., "one-click scheduling," "custom branding," "team analytics").


Step 2: Ask the Paired Kano Questions
For each feature X:

Functional question
Imagine we did offer [Feature X], what would that mean for you?

Dysfunctional question
Imagine we did not offer [Feature X], what would that mean for you?

Response scale

I like it
I expect it
I'm neutral
I can tolerate it
I dislike it


Step 3: Capture Context & Emotion
After each pair, ask one or two of these follow-ups to surface stories and feelings:

When was the last time you used or missed [Feature X]? What were you doing?
What workaround or alternative did you use instead?
How did you feel in that moment - frustrated, relieved, excited?
Who else was involved or affected when you needed this feature?


Step 4: Record & Categorize Responses
• Log each user's functional + dysfunctional answers in a spreadsheet
• Use the standard Kano evaluation table to classify each feature


Functional
+
Dysfunctional
→
Category
I expect it
+
I dislike it
→
Must-have
I like it
+
I dislike it
→
Performance
I like it
+
I am neutral
→
Attractive
I am neutral
+
I am neutral
→
Indifferent
I dislike it
+
I expect it
→
Reverse





Step 5: Summarize Key Findings
• Must-have: absence causes strong dissatisfaction - implement these first.
• Performance: satisfaction scales with level of delivery - invest to improve these.
• Attractive: unexpected delights - add selectively to surprise users.
• Indifferent: low impact - drop or deprioritize.
• Reverse: causes dislike when present - remove or rework and investigate further.


Step 6: Prioritize Your Roadmap
Must-have: ensure every must-have feature is fully built and polished.
Performance: allocate development capacity next to performance features in proportion to their impact.
Attractive: schedule 1–2 attractive (delight) features per release to boost satisfaction.
Indifferent: remove or push these items to the bottom of the backlog.
Reverse: dig deeper with user research before deciding whether to rework or eliminate.`
          },
          {
            id: "task-pmf-score",
            description: "Feedback Framework 3 - PMF Score - How disappointed would users be without your product?",
            innerContent: `The Product-Market Fit (PMF) survey is the gold-standard for product–market fit, pioneered by Sean Ellis and perfected at Superhuman under Rahul Vohra's leadership.

It zeroes in on your users' emotional dependency: if at least 40% of respondents tell you they'd be "very disappointed" without your product, you know you've hit a core need.

By quantifying "stickiness" early, you avoid wasted effort on features or marketing that don't drive true retention, and you gain a clear signal for where to double down.

Purpose  
Quickly gauge emotional dependency on your product. Only ask the PMF survey to users who have:
Used the product at least twice in the last 14 days
Or completed the core activation milestone: the one in-product event that tells you a user has actually experienced your product's promise their personal "aha" moment. 
 

Questions
Send this one question to 10 active users
1. How would you feel if you could no longer use [Product]?  
   – Very disappointed  
   – Somewhat disappointed  
   – Not disappointed
2. What type of people do you think would most benefit from [your product]?
3. What is the main benefit you receive from [your product]?
4. How can we improve [your product] for you?


2. Record responses in a sheet

3. If fewer than 40% say "Very disappointed," pause scaling and focus here next.  

Next Actions  

Interview all "Very disappointed" users (3–5):  
What's the #1 thing you get from this product?  
What part do you return for most?  
How would you describe it to a friend?

Credits: (https://review.firstround.com/how-superhuman-built-an-engine-to-find-product-market-fit/) `
          },
          {
            id: "task-jtbd",
            description: "Feedback Framework 4 - Jobs-To-Be-Done - Why they switched to, or away, from your product",
            innerContent: `In order to understand deeper goals and behavior, we need to entice users into telling us stories surrounding why they are deciding to do something. We need to probe, but we need to do so in a specific way. 
The focus to find the underlying reason of the "switch event" that lead a user to make a change. 
Depending on how invested and willing the user is to give feedback. Choose either the short or the long version of this framework.



Short version

Five-Question Switch-Event Interview
Use these five broad, story-driven prompts to capture the core reasons behind a user's switch:

Before you found us, which other tools or methods did you try, and what did you like and dislike about them?
What was happening in your work or life that pushed you to look for a new solution?
What made you decide to give our product a try instead of those other options?
What did you expect our product to do for you, and how did that match up with reality?
If our product weren't available, what would you be doing now to solve the same problem?

These five questions will uncover:
Alternatives & pain points (Q1)
Push factors (Q2)
Pull factors (Q3)
Expectations vs. experience (Q4)
Workarounds & habits (Q5)

Together, they surface the "switch event" and emotional drivers in a tight, easy-to-run interview.



Long version

What other solutions did you consider before trying the product /service?
What were you trying to find when you were considering those solutions?
What were you trying to solve?
Tell me about how you looked for these products/services.

What other solutions have you actually used?
What did and didn't you like about other solutions you've tried/used?

If this product/service wasn't available to you, what you do instead?
What solutions have the people you know tried or used?
What are you doing now compared to the past?


Start to dig deeper with more emotionally questions like:

When did you first realize you needed something to solve your problem?
Did you talk to anyone else about what choices you were considering? What was this conversation like?
Was anything recommended to you?

What did you imagine using this product/service would be like?
What did you want from the product/service?
Did you have any anxiety about purchasing/using this product/service?
Was there anything that made you nervous about using it?


While conducting the interview, listen for motivations or pain points that led to a switch:

Push of the situation
What was it about their particular situation that led them to look for a new solution?

Pull of the situation
What was it about the new solution that made them want to try it?

Habits holding them back
What habits do they have that may prevent them from trying a new solution?

Anxieties of the new solution
What anxieties did they have about trying or using a new solution?`
          },
          {
            id: "task-pricing",
            description: "Feedback Framework 5 - Find The Right Price",
            innerContent: `Anchor your price around the value you deliver. Use direct questions to pinpoint what customers see as fair. Run through these five questions in a short call or survey to uncover a "sweet spot".


Outcome Value
If our product delivered exactly what you want [specific result] what would that be worth to you per month (or per use)?

ROI Anchoring
Last year, how much did you spend (time/money) to try to get [that result]? How much did it cost you in lost opportunities?

Fair-Price Probe
Knowing you'd achieve [result], what price feels fair, where you'd feel you got your money's worth?

Too Cheap Test
What's the highest price that would feel like a bargain, too low to reflect the real value you'd receive?

Too Expensive Test
At what price would you say, 'No thanks, that's out of my budget'?


How to use this data
Plot responses to find the overlap between "fair" and "bargain" - that's your ideal entry price.
Ensure it's well below the "too expensive" threshold and high enough that "too cheap" respondents don't doubt quality.
Compare against your cost structure and customer lifetime value to finalize a price that maximizes both uptake and profitability.`
          },
          {
            id: "task-updates-test",
            description: "Feedback Framework 6 - Updates Mini-Test Loop",
            innerContent: `High-performing product teams at Figma, Notion, Linear, and even Amazon's two-pizza squads, ship small, user-driven tweaks on a weekly cadence.

This "mini-test loop" lets you validate each change with a handful of real users before investing in a full rollout. By asking just 3–5 people "What changed for you this week?" and "Did [tweak] fix what was holding you back?", you get immediate, actionable feedback and compound momentum.

It's lean, low-risk, and ensures every update moves the needle on stickiness.


Purpose
Demonstrate you're listening and iterating fast

Your Steps
Select 3 users who experienced your tweak

Ask via DM or quick survey:

What changed for you this week?
Did [new tweak] fix what was holding you back?

Next Actions
If feedback is positive, plan to roll out more broadly. If not, iterate again.`
          }
        ]
      },
      // Phase 7: First blog post
      {
        id: "phase-first-blog-post",
        number: 7,
        title: "First blog post",
        introduction: "DMs and posts give quick hits, but a blog compounds, every search impression is free traffic forever. Shipping the first article builds the habit, lets Google start crawling your domain, and gives you evergreen material to repurpose on socials.",
        keyTargets: [
          "One blog post published on your site, live URL working",
          "Post submitted and \"Indexed\" status confirmed in Google Search Console"
        ],
        tasks: [
          {
            id: "task-generate-blog",
            description: "Task 1 - Generate a blog from a topic in the Content Topics section"
          },
          {
            id: "task-add-cta",
            description: "Task 2 - Add a soft CTA at the end: \"Want early access? Join the beta here\""
          },
          {
            id: "task-publish-blog",
            description: "Task 3 - Log in to your CMS to create the blog post",
            innerContent: `Publishing your article in your CMS is how you make it discoverable, shareable, and index-ready.

Beyond simply dropping in the text, you'll fill out SEO fields like title, slug, and meta description, that search engines use to understand and rank your content. The meta description is especially important: it becomes the preview snippet in Google's results, so a clear, compelling summary here drives clicks.

Log in to your CMS
Go to your site's admin URL (e.g. yourdomain.com/wp-admin, yourdomain.com/admin, or your Headless CMS dashboard)
Enter your credentials and navigate to the "Posts" or "Blog" section

Create a New Post

Enter the Title

Set the Permalink/Slug
Edit the auto-generated URL to a concise, keyword-rich slug
Shorter is better, aim for 3–6 words

Paste & Format Your Content
Copy your final blog copy into the main editor
Use heading tags (H2, H3) for sections, bullet lists, and images to improve readability
Proper use of heading tags does more than improve readability. Search engines use those tags to understand your content's structure and hierarchy, so: H1 → H2 → H3 signals main topics and subtopics, helping Google map your keywords to the right sections

Write the Meta Description
In the SEO or "Meta" settings panel, add a 155–160 character summary of the post
Describe the key benefit or solution ("Learn the 4 steps to diagnose and fix Event Match Quality in Meta Ads, so you stop wasting ad spend."
This snippet appears under your title in search results, make it clear and compelling

Add a Featured Image & Alt Text
Upload a high-quality cover image (1200×628 px is a common size).
In the image settings, write an alt text that describes the image and includes your primary keyword

Assign Categories & Tags (if available)
Select one primary category (e.g. "Meta Ads") and 2–4 relevant tags
Helps on-site organization and can boost SEO through structured archives

Set Social Preview (if available)
Many CMSs let you customize the title, description, and image for Facebook/Twitter cards, ensure these match your SEO fields or are even more click-worthy

Preview & Publish
Click "Preview" to check formatting on desktop and mobile.
Fix any layout glitches or broken links
When everything looks good, hit "Publish" (or schedule for the desired date/time)`
          },
          {
            id: "task-request-indexing",
            description: "Task 4 - Request indexing on Google",
            innerContent: `Indexing is how search engines discover, analyze, and store your page so it can show up in search results. Without being indexed, even the best-optimized content is invisible to people searching for your keywords.

Proactively requesting indexing when you publish:

Ensures visibility: Your post can immediately appear for relevant searches
Speeds up ranking: Gets you into Google's results faster, driving traffic sooner
Enables performance tracking: Once indexed, you can monitor impressions, clicks, and position in Search Console

Steps to Request Indexing

Open Google Search Console
Navigate to https://search.google.com/search-console and select your property

Use the URL Inspection Tool
Paste your new blog post's full URL into the top bar and hit Enter

Wait for Confirmation
Google will queue your URL for crawling, this typically takes a few minutes`
          }
        ]
      },
      // Phase 8: Create business accounts
      {
        id: "phase-create-business-accounts",
        number: 8,
        title: "Create business accounts",
        introduction: "Your personal profile proves you're real, but a branded handle builds legitimacy for cold prospects, partners, and future paid ads. Locking the usernames now prevents squatters and lets you start collecting brand followers while you focus on product.",
        keyTargets: [
          "One business account or page live on each core platform for your audience type",
          "Profile image, bio, and website link added via Marketing Vault snippets",
          "30-day social calendar generated for later execution",
          "6-9 posts published on at least one of those new accounts"
        ],
        tasks: [
          {
            id: "task-create-business-account",
            description: "Task 1 - Create a business account or page on your core business platform(s)"
          },
          {
            id: "task-add-bio",
            description: "Task 2 - Add bios and descriptions from the Marketing Vault"
          },
          {
            id: "task-add-website",
            description: "Task 3 - Add your landing page or wait-list link in the website field"
          },
          {
            id: "task-generate-calendar",
            description: "Task 4 - Generate from the Social Calendar or Generate Content"
          },
          {
            id: "task-publish-posts",
            description: "Task 5 - Publish 6 to 9 post on each new business account"
          }
        ]
      },
      // Phase 9: First directory submission
      {
        id: "phase-first-directory-submission",
        number: 9,
        title: "First directory submission",
        introduction: "Google trusts domains that other sites link to. Backlinks can move your website from \"invisible\" to \"starting to rank,\" while the listing itself may drive a few curious clicks. Doing one now builds momentum without derailing product work and fast acquisition tasks.",
        keyTargets: [
          "One directory listing submitted and live (or \"pending review\")"
        ],
        tasks: [
          {
            id: "task-choose-directory",
            description: "Task 1 - Choose a directory from the Directories section that matches your product"
          },
          {
            id: "task-setup-listing",
            description: "Task 2 - Add bios and descriptions from the Marketing Vault"
          },
          {
            id: "task-submit-listing",
            description: "Task 3 - Use content from the Marketing Vault where applicable. Every directory has its own requirements"
          },
          {
            id: "task-mark-complete",
            description: "Task 4 - Mark the directory as 'Complete' in the Directory listing"
          }
        ],
        tips: [
          {
            id: "tip-submission-practices",
            content: "Submission best practices: Add one bullet list of key features in the long description for scannability"
          }
        ]
      }
    ]
  },
  
  // Cycle 1: Cold Outreach
  {
    id: "cycle-cold-outreach",
    title: "Breakthrough Cycle 1: Cold Outreach",
    goals: "Manually acquire your first users, reviews, and traction through warm outreach, social posts, and personalized cold DMs. Start with free beta users for feedback, then shift quickly to paid or trial users.",
    targets: [
      "5–10 free beta users onboarded: Invite only those who are highly likely to give feedback. Beyond this, you transition to paid users or time-limited trials.",
      "10 paying or trial users acquired via cold outreach: After the beta window, focus every DM on converting new users into paid or trial accounts.",
      "10 written reviews: Ask every happy user for one. These become assets, use them in email, landing pages, and social proof posts.",
      "15+ meaningful feedback conversations: Includes DM replies, interviews, or in-app messages. Use this input to improve onboarding and positioning.",
      "Private feedback group hits 5-10 members: Add active users into a private Slack or Discord. This keeps them engaged and gives you a real-time feedback loop."
    ],
    phases: [
      // Phase 9: Launch to Inner Circle (optional)
      {
        id: "phase-launch-to-inner-circle",
        number: 9,
        title: "Launch to Inner Circle",
        introduction: "Reach out to warm contacts with DMs, voice notes, and quick calls. The moment someone signs up, add them to a private Slack to keep feedback and momentum flowing.",
        keyTargets: [
          "DMs sent to all relevant warm contacts",
          "Three-way introductions used (via WhatsApp, Telegram, or Messenger)",
          "Private Slack created and all users invited"
        ],
        tasks: [
          {
            id: "task-list-contact-platforms",
            description: "List all the places where you have contacts (whatsapp/Telegram, Facebook, Instagram, etc.)"
          },
          {
            id: "task-message-contacts",
            description: "Go through your contact lists and start messaging the people who you think are most likely to be interested in your product. Note: Do not make a list first of people to message. Just message them to prevent procrastination. If it takes more than 10 minutes to find a prospect, move on to the next phase."
          },
          {
            id: "task-save-positive-replies",
            description: "Screenshot any positive reply and store in a folder"
          }
        ],
        tips: [
          {
            id: "tip-recommended-approach",
            content: "Recommended approach:\nHey [name], thought of you. How's it going? → Engage in casual conversation for a few messages\nBy the way, the thing that reminded me of you is... → explain what you are working on\nAsk if this is relevant to them and if they are having the problem you solve\nIf yes → \"Want early access? It's free while I gather feedback.\"\nIf no → \"Know 1–2 people who struggle with this? Could you do a quick 3-way intro?\""
          },
          {
            id: "tip-execution-tips",
            content: "Execution tips:\n\nTrack replies\nUse a simple Google Sheet as they come in, no fancy CRM yet\n\nVoice-note boost\nSend a short follow-up voice message, the familiarity spike lifts reply rates\n\n3-way intro\nAsk warm contacts to start a group chat and intro you. Far warmer than cold messaging a stranger saying you know their friend."
          }
        ]
      },
      // Phase 10: Leverage Early Feedback
      {
        id: "phase-leverage-early-feedback",
        number: 10,
        title: "Leverage Early Feedback",
        introduction: "You just onboarded your first users. Now's your moment to listen. Great products are shaped by feedback, and trust is built by showing you care. Plus, happy users can bring in new ones, if you ask the right way.",
        keyTargets: [
          "Feedback collected from 5+ users",
          "2 public posts sharing positive feedback (X, Reddit, or LinkedIn)",
          "3 personal follow-ups with happy users",
          "2 three-way intros requested via DM or chat"
        ],
        tasks: [
          {
            id: "task-collect-feedback",
            description: "Ask new users for feedback using the Mom Test model (use GPT to map the right questions for your product)"
          },
          {
            id: "task-share-feedback",
            description: "Share positive feedback publicly (X, Reddit, or LinkedIn)"
          },
          {
            id: "task-follow-up",
            description: "Follow up 1-on-1 with users who had a good experience"
          },
          {
            id: "task-request-intros",
            description: "Use a 3-way intro: \"Hey, since this is very useful to you and it's helping you achieve [desired outcome] in [ease of achievement]. Do you know 1-2 people who can benefit from this (too)? Do you think you can do a three-way intro with me?\""
          }
        ]
      },
      // Phase 11: Comment, Connect, and Convert
      {
        id: "phase-comment-connect-convert",
        number: 11,
        title: "Comment, Connect, and Convert",
        introduction: "Turn cold outreach into warm conversations by engaging with your ideal users where they're already active, then DM them right after. The goal is the DM, the comment is your entry point.\n\nThis phase is repeatable.\n\nAfter phase 12, you'll cycle back here to this phase. Getting new users, collecting feedback and reviews, repeat.",
        keyTargets: [
          "Get 5-10 users from cold outreach before continuing to the next phase",
          "Send 15+ DMs per day until this is reached. (Send 60 DMs total for this phase; then evaluate your approach)"
        ],
        tasks: [
          {
            id: "task-find-posts",
            description: "Find posts your audience follows, look for relevant comment sections. Like or comment on your target's reply. Immediately follow and send a contextual DM using the \"comment-based\" opener"
          },
          {
            id: "task-send-dms",
            description: "Comment/like and DM 15+ potential users daily"
          },
          {
            id: "task-track-replies",
            description: "Track your warm replies in a Google Sheet or CRM. These are the people who reply meaningfully, even if they don't convert (yet)"
          },
          {
            id: "task-follow-up",
            description: "Add all warm leads to a follow-up list. Don't let warm interest cool off, follow up 1-2 days later"
          },
          {
            id: "task-convert-users",
            description: "Convert at least 5 users through this flow. These can be freemium, demo, or trial users, whatever your product defines as a \"real\" user"
          }
        ],
        tips: [
          {
            id: "tip-where-to-do-this",
            content: "Where to do this:\nB2B → Comment under LinkedIn growth or SaaS posts, DM on LinkedIn or email\nDev tools → GitHub issues, Hacker News replies, dev X.com threads\nCreators → Reels replies, TikTok comments, X.com creative threads\nEcommerce → Shopify subreddits, Meta Ads comment sections, brand posts on X.com\n\nPacing:\nSpace DMs through the day (max 1 per 5 min) to avoid spam\nFollow-up once after 48 h with a new angle"
          }
        ]
      },
      // Phase 12: Review & Referral Engine
      {
        id: "phase-review-referral-engine",
        number: 12,
        title: "Review & Referral Engine",
        introduction: "People trust proof over claims. This phase multiplies your wins and can get others sharing once you make it easy.",
        keyTargets: [
          "5 written reviews collected",
          "3 social shoutouts posted",
          "2 referrals generated (via WhatsApp or intro)"
        ],
        tasks: [
          {
            id: "task-collect-reviews",
            description: "Ask 5 users for a short review (email, Slack, DM)"
          },
          {
            id: "task-post-shoutouts",
            description: "Post 3 social shoutouts featuring real users"
          },
          {
            id: "task-request-intros",
            description: "Ask 2 users for warm three-way intros after positive feedback/review (\"If this saved you [time], do you know 1-2 people this would be helpful to? Mind a quick intro?\")"
          },
          {
            id: "task-create-visuals",
            description: "Turn 2 review quotes into visuals for social media"
          },
          {
            id: "task-publish-feedback",
            description: "Publish 2 feedback screenshot posts across social"
          }
        ],
        tips: [
          {
            id: "tip-where-to-ask",
            content: "Where to ask:\nB2B → LinkedIn\nDev → GitHub, Discord\nCreators → TikTok or IG Story\nEcommerce → Twitter, product forums"
          }
        ]
      }
    ]
  },
  
  // Cycle 2: Building Momentum
  {
    id: "cycle-building-momentum",
    title: "Breakthrough Cycle 2: Building Momentum",
    goals: "Build public visibility and trust by consistently documenting progress, sharing product updates, and involving your audience in key product decisions. This attracts early adopters and creates a warm audience for future launches.",
    targets: [
      "3 users said they signed up because of your post or story: Track and log the people who signed up or took action because of your story.",
      "Build-in-public tracker launched: Create a pinned X thread, Notion doc, or blog that logs progress weekly.",
      "15 product updates published: Feature releases, personal learnings, or behind-the-scenes lessons, posted to X.com, Reddit, IndieHackers.",
      "5 product decisions influenced by feedback: Publicly share a change or improvement you made based on community input.",
      "6 polls, questions, or \"which version is better\" posts: Use your audience to test landing pages, onboarding flows, or positioning angles.",
      "100+ outbound comments, replies, or DMs: Engage meaningfully in your space to build recognition and trust."
    ],
    phases: [
      // Phase 13: Start Building in Public
      {
        id: "phase-start-building-in-public",
        number: 13,
        title: "Start Building in Public",
        introduction: "Publicly document progress and start showing up in people's feeds. Share learnings, struggles, and wins. Make your journey transparent so people start paying attention.",
        keyTargets: [
          "Build-in-public tracker launched",
          "3–5 real updates posted publicly",
          "5+ posts commented on daily for at least 5 days",
          "1 CTA-driven post published"
        ],
        tasks: [
          {
            id: "task-launch-tracker",
            description: "Launch your Build-in-Public Tracker. Start a Notion doc, GitHub log, or pinned X.com thread to log weekly updates."
          },
          {
            id: "task-ship-updates",
            description: "Ship & post 3–5 mini updates. E.g., new feature, user feedback tip, \"what broke this week\""
          },
          {
            id: "task-share-analytics",
            description: "Share analytics or challenges. Example: \"We got 31 signups this week, but only 4 converted. Time to fix onboarding.\""
          },
          {
            id: "task-add-ctas",
            description: "End every post with a CTA. Ask for feedback, early access signups, or just open DMs"
          },
          {
            id: "task-engage-other-posts",
            description: "Comment on 5–10 posts/day by other founders or creators. Real engagement builds visibility and reciprocity"
          }
        ],
        tips: [
          {
            id: "tip-where-to-build",
            content: "Where to Build in Public\nChoose the platform your audience already watches:\nB2B SaaS → Post on LinkedIn and X.com\nDev tools → Use GitHub issues, Hacker News, or IndieHackers\nCreator tools → Use X.com, Reddit, or Instagram stories"
          }
        ],
        ongoingDailyTasks: [
          {
            id: "daily-task-comment",
            title: "Comment on relevant posts",
            description: "Comment on 5–10 relevant posts per day"
          },
          {
            id: "daily-task-share-progress",
            title: "Share progress",
            description: "Share progress or small wins 3x/week"
          },
          {
            id: "daily-task-respond",
            title: "Respond to comments",
            description: "Respond to comments within 24h"
          }
        ]
      },
      // Phase 14: Build-in-Public Feedback Engine
      {
        id: "phase-build-in-public-feedback-engine",
        number: 14,
        title: "Build-in-Public Feedback Engine",
        introduction: "Visibility alone isn't enough. You need interaction. This phase turns passive lurkers into contributors. It's how you validate features, sharpen your messaging, and grow trust before asking for anything in return.",
        keyTargets: [
          "2 feedback posts published",
          "1 positioning post or poll created",
          "3 DMs sent to people who engaged",
          "1 clear product tip gathered from responses"
        ],
        tasks: [
          {
            id: "task-ask-for-feedback",
            description: "Ask for feedback 2x/week (X.com, Reddit, IndieHackers). Keep tone real, vulnerable, and specific"
          },
          {
            id: "task-run-poll",
            description: "Run 1 poll or \"Which one would you pick?\" post. Great for feature prioritization or landing page tests"
          },
          {
            id: "task-test-positioning",
            description: "Post 1 positioning test this week. E.g., \"Would you rather save time or get better data?\""
          },
          {
            id: "task-dm-engaged-users",
            description: "DM 3 people who engaged with your public posts. Turn votes and likes into conversations"
          }
        ],
        tips: [
          {
            id: "tip-where-to-ask",
            content: "Where & What to Ask\nBuilding for marketers? → Test CTAs or landing pages on LinkedIn or GrowthHackers\nDev-focused? → Share feature flows or product ideas on GitHub or Hacker News\nCreator-oriented? → Use story polls or X.com tweets to ask about pain points\n\nWhat types of content to post?\nPositioning vs features\nOnboarding steps or signup flow\nPricing options vs value statements"
          }
        ]
      }
    ]
  },
  
  // Cycle 3: Written Content Cluster
  {
    id: "cycle-written-content-cluster",
    title: "Breakthrough Cycle 3: Written Content Cluster",
    goals: "Build long-term organic traffic, authority, and repurposable assets by publishing educational, problem-solving, and curiosity-driven written content.",
    targets: [
      "10 SEO-focused blog posts published: Target keywords users search for: problems, comparisons, categories. Prioritize clarity and depth over speed.",
      "Internal links added across blog posts: Improve crawlability and help Google understand your content structure.",
      "All blog posts indexed and ranking in Google Search Console: Measure traction with impressions and keyword positions.",
      "5 blog posts repurposed and shared across X.com, Reddit, or email: Don't just write, distribute to where your audience lives.",
      "3 listicle-style blog posts published: E.g., \"Top 5 X Tools,\" \"Best Platforms for Y.\" These drive traffic and shape perception.",
      "20 short-form posts distributed across X.com, Reddit, Threads: Turn reviews, headlines, and pain points into punchy social content.",
      "1 pinned X.com thread: Highlight your top posts or learnings, update it every few weeks."
    ],
    phases: [
      // Phase 15: Blog Engine Kickoff
      {
        id: "phase-blog-engine-kickoff",
        number: 15,
        title: "Blog Engine Kickoff",
        introduction: "Now that you've built early traction and are getting user feedback, you need to start compounding visibility through search. Blogging creates permanent assets that drive traffic over time, especially when paired with smart distribution.",
        keyTargets: [
          "1 SEO blog post published and live",
          "URL submitted & \"Indexed\" status showing in Google Search Console"
        ],
        tasks: [
          {
            id: "task-create-blog-post",
            description: "Use the \"Generate Blog\" tool to create your first SEO blog post"
          },
          {
            id: "task-optimize-blog",
            description: "Edit and optimize the blog: include a clear title, H1, meta description, and keyword-based slug"
          },
          {
            id: "task-publish-blog",
            description: "Publish the post on your website or blog"
          },
          {
            id: "task-submit-to-gsc",
            description: "Submit the URL to Google Search Console for indexing"
          },
          {
            id: "task-confirm-indexing",
            description: "Confirm the post is indexed in Google Search Console (This can take 24-72 h; log the date but move to the next phase immediately after submission.)"
          }
        ],
        tips: [
          {
            id: "tip-blog-topics",
            content: "Blog Topics\nB2B SaaS → Feature comparisons, pain-solution explainers\nDev Tools → Integration guides, performance deep-dives, \"how it works\"\nCreator Tools → Strategy tips, content hacks, \"Top X\" listicles"
          }
        ]
      },
      // Phase 16: Daily Written Posts
      {
        id: "phase-daily-written-posts",
        number: 16,
        title: "Daily Written Posts",
        introduction: "Blog posts take time to rank, but short-form posts deliver feedback and visibility fast. These give you daily reach while sharpening your messaging.",
        keyTargets: [
          "10-15 short-form posts published across ≥ 2 platforms",
          "Top-performing post format logged for reuse"
        ],
        tasks: [
          {
            id: "task-publish-short-posts",
            description: "Publish 2–3 written posts per day for 5 consecutive days on at least 2 platforms"
          },
          {
            id: "task-use-generator",
            description: "Use the \"Written Post\" generator or repurpose blog content, user quotes, or product updates"
          },
          {
            id: "task-include-cta",
            description: "Include a CTA in every post (\"Try it\", \"Would you use this?\", \"See how it works?\")"
          },
          {
            id: "task-track-engagement",
            description: "Track total likes, comments, and DMs for each post using a simple table or Notion board"
          },
          {
            id: "task-reuse-format",
            description: "Identify your top-performing post and reuse its format in a follow-up within 3 days"
          }
        ],
        tips: [
          {
            id: "tip-post-angle",
            content: "Post Angle\nB2B SaaS → Workflow fixes, cost-savings, decision-making tips\nDev Tools → Performance wins, GitHub success stories, bug war stories\nCreator Tools → Social growth tips, tool hacks, content workflows"
          }
        ],
        ongoingDailyTasks: [
          {
            id: "daily-task-publish-posts",
            title: "Publish short-form posts",
            description: "Publish 2–3 short-form posts across platforms"
          },
          {
            id: "daily-task-track-engagement",
            title: "Track post performance",
            description: "Track likes, replies, and DMs daily to identify what hits"
          },
          {
            id: "daily-task-reuse-formats",
            title: "Reuse successful formats",
            description: "Reuse successful formats every 2–3 days"
          }
        ]
      },
      // Phase 17: Listicle Hijacking
      {
        id: "phase-listicle-hijacking",
        number: 17,
        title: "Listicle Hijacking",
        introduction: "You've earned trust, now control the narrative. People search for \"Best tools for X.\" If you write the post, you choose who wins. Plus, these posts naturally drive backlinks and shares.",
        keyTargets: [
          "1 listicle post live with product ranked #1"
        ],
        tasks: [
          {
            id: "task-write-listicle",
            description: "Write and publish 1 blog post titled \"Top 5 Tools for [Your Category]\" or similar, with your product listed as #1"
          },
          {
            id: "task-add-media",
            description: "Include at least 1 screenshot, testimonial, or demo video to justify your #1 position"
          },
          {
            id: "task-share-post",
            description: "Share the post across 3 or more platforms or communities where your users hang out (e.g., Reddit, X.com, a relevant directory, LinkedIn)"
          },
          {
            id: "task-track-views",
            description: "Track total views, comments, or clicks from each distribution channel over 5 days"
          },
          {
            id: "task-respond-to-comments",
            description: "Respond to any comments or engagement generated by your post within 24 hours"
          }
        ],
        tips: [
          {
            id: "tip-where-to-distribute",
            content: "Where to Distribute\nDev Tools → Hacker News, GitHub repos, Reddit dev subs\nB2B SaaS → LinkedIn posts, B2B newsletters, founder groups\nCreator Tools → Instagram carousels, Twitter threads, TikTok tool stack videos"
          }
        ]
      }
    ]
  },
  
  // Cycle 4: Lead Magnet & Email Engine
  {
    id: "cycle-lead-magnet-email-engine",
    title: "Breakthrough Cycle 4: Lead Magnet & Email Engine",
    goals: "Capture emails from blog and social traffic, then convert them into users through automated, value-driven emails. Build an owned audience that compounds over time.",
    targets: [
      "1 lead magnet created and launched: Checklist, cheat sheet, guide, or swipe file tailored to your audience's biggest pain",
      "1 high-converting opt-in page published: Clear headline, benefit-driven copy, image or preview, CTA button, and optional proof",
      "3+ lead magnet CTAs added: Blog posts, social bios, email footers, or Reddit comments",
      "5-part welcome sequence written and automated: Mix of value, story, and product positioning",
      "1 nurture email sent each week for 4 weeks: Keep the list warm with updates, use cases, product wins, or related blog content",
      "50+ email leads captured: From social traffic, blog CTAs, Reddit posts, or giveaways",
      "1 exclusive offer promoted to your list: Discount, bonus, or invite-only feature to test conversion potential",
      "Track basic email metrics: Open rate, click-through rate, unsubscribe rate, conversion"
    ],
    phases: [
      // Phase 18: Create & Launch Lead Magnet
      {
        id: "phase-create-launch-lead-magnet",
        number: 18,
        title: "Create & Launch Lead Magnet",
        introduction: "You've started attracting attention through blog posts, social proof, and daily content. Now it's time to capture that interest before it disappears—by offering something helpful in exchange for an email.",
        keyTargets: [
          "1 lead magnet created",
          "1 opt-in page published",
          "CTAs placed on blog posts, Reddit replies, X.com content, and social bios"
        ],
        tasks: [
          {
            id: "task-choose-format",
            description: "Choose a format: checklist, swipe file, mini-guide, use case playbook, report, etc."
          },
          {
            id: "task-build-optin-page",
            description: "Build an opt-in page where people can leave their email in exchange for the lead magnet"
          },
          {
            id: "task-add-ctas",
            description: "Add lead magnet CTAs across blog posts, Reddit replies, X.com content, and social bios"
          },
          {
            id: "task-connect-email-tool",
            description: "Connect the opt-in page to your email tool (ConvertKit, Beehiiv, etc.) and set an auto-delivery email or redirect so subscribers get the lead magnet instantly."
          }
        ],
        tips: [
          {
            id: "tip-lead-magnet-type",
            content: "Lead Magnet Type\nChoose your giveaway based on what your audience would actually use:\nDev tools → Debug checklist, \"Top 100 productivity shortcuts\" swipe file\nB2B SaaS → ROI calculator, funnel teardown guide, cold outreach template\nCreator tools → Notion workspace, growth PDF, \"Top 10 Hooks\" swipe file"
          }
        ]
      },
      // Phase 19: Build & Automate Welcome Sequence
      {
        id: "phase-build-automate-welcome-sequence",
        number: 19,
        title: "Build & Automate Welcome Sequence",
        introduction: "Collecting emails is great. But if people don't hear from you again, or hear the wrong message, you'll lose them. A welcome sequence builds trust and sets you up for future conversions. Focus on giving value before going for the ask or making them a hard offer.",
        keyTargets: [
          "Email sequence of 3-5 emails",
          "Sequence is live and sending",
          "Each email includes a soft CTA"
        ],
        tasks: [
          {
            id: "task-use-email-templates",
            description: "Use LaunchGuide 3–5 email templates (value-first, not salesy)"
          },
          {
            id: "task-automate-sequence",
            description: "Automate the sequence using your email platform (Klaviyo, Beehiiv, ConvertKit, etc.)"
          },
          {
            id: "task-add-soft-cta",
            description: "Add a soft CTA to each email (\"Try demo\", \"Explore feature\", \"Join early access\")"
          },
          {
            id: "task-set-trigger",
            description: "Set the trigger to 'new subscriber to lead-magnet list' (or similar) and send a test to yourself to confirm each email delivers."
          }
        ]
      },
      // Phase 20: Weekly Nurture Loop
      {
        id: "phase-weekly-nurture-loop",
        number: 20,
        title: "Weekly Nurture Loop",
        introduction: "Most people won't convert immediately. Automated weekly emails give you a reason to stay in their world, and when they're ready, they'll already trust you.",
        keyTargets: [
          "Send 1 email per week for 4 consecutive weeks",
          "Each email provides value and includes a soft CTA",
          "Highlight at least 2 pieces of repurposed content"
        ],
        tasks: [
          {
            id: "task-generate-weekly-emails",
            description: "Use LaunchGuide to generate 1 email for each week (value, lesson, case study, or update)"
          },
          {
            id: "task-resurface-content",
            description: "Resurface blog content, product updates, or success stories"
          },
          {
            id: "task-include-feedback-prompts",
            description: "Include feedback prompts or links to revisit the product"
          },
          {
            id: "task-setup-weekly-automation",
            description: "Set up automation in your email platform to send 1 email per week"
          }
        ],
        tips: [
          {
            id: "tip-focus-by-subscriber",
            content: "Focus by Subscriber Type\nEarly users → Feature updates, mini case studies\nNew subs → Onboarding help, top blog posts\nInactives → Winback stories, \"What's changed\" update"
          }
        ]
      },
      // Phase 21: Warm List Monetization
      {
        id: "phase-warm-list-monetization",
        number: 21,
        title: "Warm List Monetization",
        introduction: "You've built trust. Now it's time to make the ask. You're not blasting cold traffic, you're inviting people who already like you to take the next step.",
        keyTargets: [
          "Subscriber-only offer launched"
        ],
        tasks: [
          {
            id: "task-create-special-offer",
            description: "Create a special offer for subscribers only (discount, bonus content, extended trial)"
          },
          {
            id: "task-create-email-campaign",
            description: "Create the email campaign and send it to your whole list"
          }
        ]
      }
    ]
  },
  
  // Cycle 5: Video Content Cluster
  {
    id: "cycle-video-content-cluster",
    title: "Breakthrough Cycle 5: Video Content Cluster",
    goals: "Build trust, explain the product, and expand visibility with high-leverage video content.",
    targets: [
      "3 product explainer videos published: Loom-style walkthroughs showing how it works in real life. Upload full versions to YouTube.",
      "15+ short-form video clips published: Repurpose tutorials, screen recordings, UGC, or founder talking-heads into native Reels, Shorts, TikToks, and X.com clips.",
      "5 user-generated video testimonials collected: Ask satisfied users weekly. Incentivize if needed. These build trust and power retargeting ads.",
      "1 viral-style video tested each week: Try trend jacking, founder transparency, behind-the-scenes content, or storytelling formats.",
      "1 high-performing video reused as a retargeting ad: Use your best organic content to warm up paid traffic and reinforce trust.",
      "3 high-quality UGC videos featured on landing pages or in emails: Increase social proof and engagement through native trust content.",
      "1 repeatable video content system created: Example: record 1x/week, batch-edit into 5+ clips, distribute across all platforms consistently.",
      "Performance metrics tracked internally: Monitor hook retention, click-through from embeds, and conversions on video-boosted pages."
    ],
    phases: [
      // Phase 22: UGC Collection Engine
      {
        id: "phase-ugc-collection-engine",
        number: 22,
        title: "UGC Collection Engine",
        introduction: "You've already gathered written reviews. This phase turns that social proof into powerful visual content. UGC performs exceptionally well in both organic and paid distribution, because it's relatable, low-friction, and high-trust.",
        keyTargets: [
          "3 UGC videos received from distinct users",
          "2 UGC snippets published on landing page / social",
          "1 UGC clip repurposed as paid-ad creative and queued for phase 26"
        ],
        tasks: [
          {
            id: "task-reach-out-to-users",
            description: "Reach out to 5+ satisfied users with a direct ask for a 30–60 second testimonial or screen-recorded walkthrough"
          },
          {
            id: "task-collect-ugc-clips",
            description: "Aim for 3 UGC video clips from different users (testimonials, demos, or walkthroughs)"
          },
          {
            id: "task-offer-incentive",
            description: "Offer an incentive (e.g., 1 free month or a public shoutout) to increase participation"
          },
          {
            id: "task-publish-ugc-snippets",
            description: "Publish at least 2 UGC snippets on your landing page and 2 on social or ads"
          },
          {
            id: "task-track-engagement",
            description: "Track engagement (clicks, replies, shares, watch time) on UGC posts for 7 days"
          }
        ],
        tips: [
          {
            id: "tip-what-to-ask",
            content: "What to Ask\nCreator tools → Ask for \"Here's how I use it\" clips\nB2B tools → Request LinkedIn testimonials or async video walkthroughs\nDev tools → Ask for casual demo videos, voiceover optional"
          }
        ]
      },
      // Phase 23: Screenrecording-Style Product Explainers
      {
        id: "phase-screenrecording-style-product-explainers",
        number: 23,
        title: "Screenrecording-Style Product Explainers",
        introduction: "By now, people are curious, but might still be unsure what your product actually does. This phase answers that question with real, visual clarity. It reduces friction and raises conversion by showing the experience.",
        keyTargets: [
          "2 full-length explainers uploaded to YouTube (public or unlisted)",
          "6 short clips cut and posted across 3 platforms"
        ],
        tasks: [
          {
            id: "task-record-explainers",
            description: "Record 2 product explainer videos showing core flows or key outcomes"
          },
          {
            id: "task-add-subtitles",
            description: "Add subtitles and captions using your preferred tool (e.g., Tella, ScreenStudio, or CleanShot)"
          },
          {
            id: "task-upload-to-youtube",
            description: "Upload both full videos to YouTube with optimized titles and descriptions"
          },
          {
            id: "task-create-short-clips",
            description: "Cut each full video into 3–5 short clips and publish across TikTok, Reels, X.com, and Shorts"
          },
          {
            id: "task-track-platform-performance",
            description: "Track views and engagement on each platform for 7 days to identify top performers"
          }
        ],
        tips: [
          {
            id: "tip-what-to-show",
            content: "What to Show\nB2B SaaS → Tutorials showing outcome or workflow savings\nDev tools → Installation, output, or CLI use\nCreator tools → How to go from idea to result in 3 steps"
          }
        ]
      },
      // Phase 24: Viral Short-Form Distribution
      {
        id: "phase-viral-short-form-distribution",
        number: 24,
        title: "Viral Short-Form Distribution",
        introduction: "You've built assets and trust, now it's time to amplify. Platforms like Instagram and TikTok reward storytelling, emotion, and visual pacing. This is how you scale reach without relying on paid ads yet.",
        keyTargets: [
          "5 short-form videos published inside one week"
        ],
        tasks: [
          {
            id: "task-publish-short-form-videos",
            description: "Publish 3–5 short-form videos this week using UGC, tutorial snippets, or founder clips"
          },
          {
            id: "task-use-emotional-angles",
            description: "Each video must follow one emotional angle: pain, transformation, or curiosity"
          },
          {
            id: "task-try-trending-formats",
            description: "Use 1 trending audio, 1 fast-caption format, and 1 proven hook structure in at least 2 of the videos"
          },
          {
            id: "task-post-across-platforms",
            description: "Post on TikTok, Reels, Shorts, and X.com within 24h of each edit"
          },
          {
            id: "task-track-video-metrics",
            description: "Track views, shares, and comments for each video across all platforms for 5 days"
          }
        ],
        tips: [
          {
            id: "tip-tone-and-format",
            content: "Tone + Format\nDev tools → \"dev pain\" memes, sarcastic rants, workflow hacks\nB2B → Founder rants, screen-shared tips, behind-the-scenes posts\nCreator tools → \"3-step tutorial\" reels, screenwalks, IG collabs"
          }
        ],
        ongoingDailyTasks: [
          {
            id: "daily-task-post-video",
            title: "Post short-form video",
            description: "Post 1 short-form video per day"
          },
          {
            id: "daily-task-comment-on-viral-posts",
            title: "Comment on viral posts",
            description: "Comment on viral posts in your niche"
          },
          {
            id: "daily-task-save-trending-audio",
            title: "Save trending audio",
            description: "Save trending audio for next week's batch"
          }
        ]
      }
    ]
  },
  
  // Cycle 6: Paid Acquisition & Partnership Engine
  {
    id: "cycle-paid-acquisition-partnership",
    title: "Breakthrough Cycle 6: Paid Acquisition & Partnership Engine",
    goals: "Rapidly scale user growth through high-leverage paid traffic, warm partnerships, and structured affiliate programs.",
    targets: [
      "1 ad platforms live: Meta - Set up lean test campaigns using your strongest organic video creatives.",
      "CAC tracking set up and monitored weekly - Track cost-per-acquisition using LaunchGuide or a simple spreadsheet. Know what every signup costs you.",
      "1 cold targeting test per platform - Validate different audience hooks with low-budget cold campaigns.",
      "1 retargeting ad launched using organic top-performers - Use existing UGC or Loom explainers to warm up visitors who didn't convert.",
      "5 external features or shoutouts published - Land podcast mentions, newsletter blurbs, blog shoutouts, or influencer tweets—fast-trust traction.",
      "20+ partnership pitches sent - Focus on high-alignment partners (creators, SaaS tool roundups, newsletters). Send value-first outreach.",
      "Affiliate & referral program launched - Promote it publicly. Use tools like Rewardful, FirstPromoter, or create a manual code system.",
      "10 affiliates onboarded - Ideal partners: micro-influencers, power users, bloggers, or people in your feedback group.",
      "1 weekly optimization cycle running - Adjust ad creatives, test new audiences, analyze CAC. Never let campaigns sit stale.",
      "Exclusive offer created for paid or partnership channels - Create urgency with an \"early adopter deal,\" \"founder pack,\" or one-time bonus stack."
    ],
    phases: [
      // Phase 25: Paid Ads Foundation
      {
        id: "phase-paid-ads-foundation",
        number: 25,
        title: "Paid Ads Foundation",
        introduction: "You've built trust through content, UGC, and product explainers. Now it's time to test performance at scale. Meta Ads give you speed, flexibility, and unmatched reach, all with one platform. This isn't about scaling big, it's about validating what works.",
        keyTargets: [
          "Meta Ad account created and verified",
          "1 cold and 1 retargeting campaign live (minimum $50 total test spend)",
          "CTR ≥ 0.75 % on best ad, CAC recorded for both campaigns",
          "Pixel/events firing correctly and visible in Ads Manager"
        ],
        tasks: [
          {
            id: "task-create-ad-account",
            description: "Create your Meta Ads account (if not done already)"
          },
          {
            id: "task-launch-video-creatives",
            description: "Launch 1–2 video creatives (UGC snippet, Loom demo, or testimonial mashup)"
          },
          {
            id: "task-set-up-cold-campaign",
            description: "Set up 1 cold campaign targeting interest groups and lookalikes"
          },
          {
            id: "task-track-metrics",
            description: "Track CAC, CTR, and conversions in LaunchGuide or a spreadsheet"
          },
          {
            id: "task-create-retargeting-campaign",
            description: "Create 1 warm retargeting campaign for visitors, engagers, and email leads"
          }
        ],
        tips: [
          {
            id: "tip-who-to-target",
            content: "Who to Target First\nB2B SaaS → Interests like \"SaaS founders\", \"marketing software\", or competitor brands\nDev tools → Interests like \"GitHub\", \"APIs\", \"software development\"\nCreator tools → \"Notion users\", \"content creators\", \"Instagram growth\"\nEcommerce SaaS → \"Shopify\", \"Meta Ads\", \"DTC founders\", or ecom-focused podcasts/publications"
          }
        ]
      },
      // Phase 26: Partnership Outreach Sprint
      {
        id: "phase-partnership-outreach-sprint",
        number: 26,
        title: "Partnership Outreach Sprint",
        introduction: "Partnerships are a smart bridge between organic and paid. Warm traffic from curated audiences converts better and costs less. These features also build credibility.",
        keyTargets: [
          "25 personalised pitches sent",
          "Outcomes logged in tracker for message optimisation (goal ≥ 20% replies / 3 features, but not required to proceed)"
        ],
        tasks: [
          {
            id: "task-identify-creators",
            description: "Identify and list 25 aligned creators (newsletters, podcasts, X.com voices, bloggers) inside a Notion or spreadsheet"
          },
          {
            id: "task-send-pitches",
            description: "Write and send 25 personalized partnership pitches with a clear value offer (early access, affiliate rev-share, or founder story angle)"
          },
          {
            id: "task-follow-up",
            description: "Follow up on all unanswered pitches within 5 days"
          },
          {
            id: "task-secure-features",
            description: "Secure at least 3 confirmed features, shoutouts, or backlinks"
          },
          {
            id: "task-document-outcomes",
            description: "Document replies and outcomes for each pitch to refine messaging"
          }
        ],
        tips: [
          {
            id: "tip-targeted-outreach",
            content: "Targeted Outreach\nDev tools → Stack Overflow creators, dev podcasts, open-source roundups\nB2B → LinkedIn creators, SaaS founders, newsletter curators (e.g. GrowthMemo)\nCreator tools → Productivity YouTubers, Notion influencers, indie creators on X.com\nEcommerce → Shopify communities, Meta Ads influencers, brand-focused podcasts"
          }
        ]
      },
      // Phase 27: Affiliate & Referral Program Launch
      {
        id: "phase-affiliate-referral-program-launch",
        number: 27,
        title: "Affiliate & Referral Program Launch",
        introduction: "You've earned trust, now make it spread. Referrals and affiliates scale what's already working and convert better than cold traffic because the trust is built-in.",
        keyTargets: [
          "Tracking tool live and tested (first dummy conversion recorded)",
          "Public affiliate page or in-app prompt published",
          "First affiliate-driven signup or trial created",
          "10 affiliates onboarded (goal: 10 affiliates / 1 signup, but not required to advance)"
        ],
        tasks: [
          {
            id: "task-choose-tracking-system",
            description: "Choose and configure a tracking system (Rewardful, FirstPromoter, or UTM-based spreadsheet)"
          },
          {
            id: "task-define-incentive-structure",
            description: "Define and publish your incentive structure (e.g. 30% lifetime rev share, 1 month free, bonus feature unlock)"
          },
          {
            id: "task-add-referral-links",
            description: "Add referral or affiliate signup links inside your product, landing page, and onboarding emails"
          },
          {
            id: "task-create-promotional-posts",
            description: "Create and publish 3 promotional posts across X.com, LinkedIn, or email"
          },
          {
            id: "task-invite-users",
            description: "Personally invite at least 10 users or partners who've shown high engagement or satisfaction"
          }
        ],
        tips: [
          {
            id: "tip-position-program",
            content: "How to Position the Program\nB2B → Offer formal affiliate dashboards, payouts, and invoices\nDev tools → GitHub README badges, in-product links\nCreator tools → Share UTM links for bios on TikTok/YouTube\nEcommerce SaaS → Reward per brand signup, tiered bonuses"
          }
        ]
      },
      // Phase 28: Paid Ads Optimization & Scaling
      {
        id: "phase-paid-ads-optimization",
        number: 28,
        title: "Paid Ads Optimization & Scaling",
        introduction: "Once your early ads show promise, it's time to scale. This phase helps you confidently increase spend while keeping CAC under control and ROAS high.",
        keyTargets: [
          "Weekly ad-report doc created and filled (CPC, CTR, CAC, ROAS)",
          "1 new creative variant launched, 1 loser paused each week"
        ],
        tasks: [
          {
            id: "task-review-ad-performance",
            description: "Review ad performance weekly (CPC, CTR, CAC, ROAS) using Meta Ads Manager or your tracking sheet"
          },
          {
            id: "task-duplicate-top-performer",
            description: "Identify your top-performing creative and duplicate it with 2–3 hook or angle variations"
          },
          {
            id: "task-launch-retargeting",
            description: "Launch 1 retargeting campaign using video viewers, email list, or website traffic"
          },
          {
            id: "task-test-urgency-angle",
            description: "Test 1 urgency-based angle this week (limited-time bonus, discount, countdown, etc.)"
          },
          {
            id: "task-optimize-budget",
            description: "Pause the worst-performing ad set(s) and reallocate budget to top performers"
          }
        ],
        tips: [
          {
            id: "tip-refined-ad-angles",
            content: "Refined Ad Angles\nDev tools → Speed demos, terminal outputs, comparison hooks\nB2B → ROI visuals, testimonial carousels, case study highlights\nCreator tools → Before/after transformations, workflow speedups\nEcommerce → Revenue leak hooks, \"Brands doing X are missing Y\""
          }
        ]
      }
    ]
  },
  
  // Cycle 7: Retention & Expansion Engine
  {
    id: "cycle-retention-expansion",
    title: "Breakthrough Cycle 7: Retention & Expansion Engine",
    goals: "Maximize lifetime value, reduce churn, and turn power users into growth agents through smart onboarding, segmentation, and upsell loops.",
    targets: [
      "3 onboarding friction points rephased - Use user feedback, recordings, or drop-off data to fix blockers and speed up first wins.",
      "1 activation metric defined and tracked - E.g., \"created first campaign,\" \"connected data source,\" \"invited teammate.\"",
      "Retention email sequence live - Include small-win celebrations, re-engagement flows, and weekly power tips.",
      "Activation rate benchmarked - What % of new users reach the core success action within 7 days?",
      "3 user segments created - Power users, at-risk users, and trialing users. Used for in-app personalization or CRM targeting.",
      "1 upsell path visible inside app - Soft CTA for upgrade, usage cap, or unlockable feature.",
      "1 habit-forming feature highlighted - Show users how to use a power feature that keeps them coming back.",
      "Soft upsell nudges sent - Triggered emails or in-app prompts that gently push toward expansion.",
      "3 power users spotlighted - Feature in newsletter, social, or testimonials.",
      "1 advocacy reward delivered - Bonus, discount, or public shoutout for a user who referred or promoted you."
    ],
    phases: [
      // Phase 29: Onboarding Flow Tune-Up
      {
        id: "phase-onboarding-flow-tune-up",
        number: 29,
        title: "Onboarding Flow Tune-Up",
        introduction: "You've worked hard to acquire users. A poor onboarding experience will waste that effort. This phase ensures users reach a key milestone quickly and understand your product's value.",
        keyTargets: [
          "Microsoft Clarity installed",
          "3+ onboarding friction points identified and improved",
          "1 activation metric defined (e.g., \"connected integration\")",
          "Activation rate benchmarked across users"
        ],
        tasks: [
          {
            id: "task-install-clarity",
            description: "Install Microsoft Clarity (free tool)"
          },
          {
            id: "task-analyze-dropoff",
            description: "Analyze onboarding drop-off using heatmaps, feedback, or session replays"
          },
          {
            id: "task-add-tooltips",
            description: "Add tooltips, walkthroughs, or \"first win\" prompts"
          },
          {
            id: "task-define-activation",
            description: "Define your activation event (e.g., \"created first campaign\")"
          },
          {
            id: "task-measure-activation",
            description: "Measure how many users hit that event within their first 7 days"
          }
        ]
      },
      // Phase 30: Retention Email Series
      {
        id: "phase-retention-email-series",
        number: 30,
        title: "Retention Email Series",
        introduction: "Even satisfied users can drift. This phase keeps them close and reminds them of the value they're getting. It also resurfaces features they may have missed.",
        keyTargets: [
          "1 win-celebration email live",
          "1 re-engagement flow triggered for inactives",
          "Weekly email cadence started"
        ],
        tasks: [
          {
            id: "task-create-celebration-emails",
            description: "Create 1–2 emails that celebrate success moments (\"You tracked 5 users!\")"
          },
          {
            id: "task-define-trigger-segment",
            description: "Define the trigger segment: users whose 'last activity/date-opened' is > 7 days, so the re-engagement email fires automatically"
          },
          {
            id: "task-schedule-weekly-emails",
            description: "Schedule weekly emails with tips, feature highlights, or mini-tutorials"
          }
        ]
      },
      // Phase 31: Expansion Campaigns
      {
        id: "phase-expansion-campaigns",
        number: 31,
        title: "Expansion Campaigns",
        introduction: "Happy users are more likely to pay more. Expansion is the most efficient way to increase revenue without acquiring new users.",
        keyTargets: [
          "1 upsell banner or in-product CTA live",
          "1 upgrade offer or \"what you're missing\" email sent",
          "Expansion incentives tested"
        ],
        tasks: [
          {
            id: "task-add-upsell-prompts",
            description: "Add in-product upsell prompts based on usage (Quick start: show upsell when the user hits 75% of their free quota or completes 3 sessions)"
          },
          {
            id: "task-email-engaged-users",
            description: "Email engaged users with limited-time or feature-based upgrade offers"
          },
          {
            id: "task-test-incentives",
            description: "Test benefits like bonus credits or feature bundles"
          }
        ]
      },
      // Phase 32: User Segmentation & Personalization
      {
        id: "phase-user-segmentation-personalization",
        number: 32,
        title: "User Segmentation & Personalization",
        introduction: "Generic messaging causes drop-off. Segmentation gives each user what they need—whether that's help, a push, or an advanced feature.",
        keyTargets: [
          "3 segments identified (e.g., power users, at-risk, trial users)",
          "Personalized messages or flows activated per segment",
          "Lifecycle-based CTAs live"
        ],
        tasks: [
          {
            id: "task-identify-segments",
            description: "Identify key user segments by behavior or plan (Quick start: tag users as 'power' = ≥3 log-ins in last 7 days, 'at-risk' = no activity in 14 days, 'trial' = still on free plan; you can create these tags in Intercom, Customer.io, or even a simple spreadsheet)"
          },
          {
            id: "task-customize-flows",
            description: "Customize onboarding or in-app flows for beginners vs pros"
          },
          {
            id: "task-adjust-ctas",
            description: "Adjust CTAs like \"Invite team\" or \"Explore analytics\" based on activity"
          }
        ]
      },
      // Phase 33: Advocacy Loop
      {
        id: "phase-advocacy-loop",
        number: 33,
        title: "Advocacy Loop",
        introduction: "Delighted users want to share, if you give them a reason and make it easy. This phase converts silent fans into public champions.",
        keyTargets: [
          "3 power users identified and activated",
          "1 case study or public testimonial created",
          "1 advocacy reward delivered"
        ],
        tasks: [
          {
            id: "task-find-top-users",
            description: "Use engagement or usage data to find top users (Quick start: mark anyone in the top 10 % of log-ins or feature usage over the last 30 days as a power user, use product analytics, a simple SQL query, or export to a spreadsheet)"
          },
          {
            id: "task-ask-for-review",
            description: "Ask them for a review, case study, or affiliate referral"
          },
          {
            id: "task-reward-advocates",
            description: "Reward with shoutouts, perks, or exclusive bonuses"
          }
        ]
      }
    ]
  },
  
  // Cycle 8: Launch & Viral Amplification Engine
  {
    id: "cycle-viral-amplification",
    title: "Breakthrough Cycle 8: Viral Amplification Engine",
    goals: "Drive a major spike in visibility, trust, and signups through a high-impact public launch. Leverage existing users, content, and platforms to create shareable assets and viral loops that fuel long-term growth.",
    targets: [
      "1 Product Hunt launch executed - PH is the core platform. All other channels support it with awareness and distribution.",
      "1,000+ unique visitors during launch week - Measured via Google Analytics or LaunchGuide tracking. Reflects launch reach and engagement.",
      "3 supporting posts published - Teaser or breakdown posts on X.com, Reddit, or IndieHackers to fuel awareness—not as primary launch platforms.",
      "1 teaser or demo video created - A high-converting launch trailer, Loom demo, or founder talking-head. Used in your PH page, landing page, and social.",
      "25+ social/community shares triggered - Includes reposts, retweets, Discord mentions, LinkedIn shares, or newsletter shoutouts.",
      "1 referral or viral loop launched - Could be a simple invite page, unlockable bonus, or leaderboard shared in onboarding and email.",
      "5 key takeaways documented - Based on comments, sign-up behavior, launch stats, or feedback—used to refine messaging, onboarding, or positioning."
    ],
    phases: [
      // Phase 34: Launch Campaign Planning
      {
        id: "phase-launch-campaign-planning",
        number: 34,
        title: "Launch Campaign Planning",
        introduction: "You've built trust and traction, now it's time to go public. This launch aligns your existing assets to drive massive visibility in a short time.",
        keyTargets: [
          "7-day cross-channel content calendar drafted & approved",
          "Invite at least 40 potential advocates and log responses",
          "Track for Insight (optional): Note how many confirm (goal ≥ 40, but not required to advance, launch can proceed with any committed group)",
          "Product Hunt draft page complete (title, tagline, media, first comment)",
          "Launch checklist signed off by team (logistics, assets, timezones)"
        ],
        tasks: [
          {
            id: "task-choose-platform",
            description: "Choose Product Hunt as your launch platform"
          },
          {
            id: "task-plan-pre-launch",
            description: "Use Reddit, X.com, and your email list to fuel pre-launch hype"
          },
          {
            id: "task-create-content-calendar",
            description: "Create a 7-day content calendar (teasers, launch push, follow-up)"
          },
          {
            id: "task-recruit-advocates",
            description: "Recruit 40-50+ advocates and prepare them with messaging"
          },
          {
            id: "task-schedule-pushes",
            description: "Schedule coordinated pushes across all platforms"
          }
        ],
        tips: [
          {
            id: "tip-segmentation-prompt",
            content: "Segmentation Prompt\nB2B SaaS → PH + LinkedIn\nDev Tools → PH + Hacker News\nCreator Tools → PH + IndieHackers, X.com"
          }
        ]
      },
      // Phase 35: Launch Asset Preparation
      {
        id: "phase-launch-asset-preparation",
        number: 35,
        title: "Launch Asset Preparation",
        introduction: "The biggest risk to a launch isn't lack of interest, it's scrambling last-minute. This phase makes sure you show up ready.",
        keyTargets: [
          "Final PH graphics, teaser video, and copy exported & stored in \"/Launch Assets\"",
          "Landing-page launch variant live & mobile-optimised",
          "5 testimonial screenshots ready for social proofs",
          "All scheduled social posts loaded into queue/drafts"
        ],
        tasks: [
          {
            id: "task-create-ph-post",
            description: "Create Product Hunt post"
          },
          {
            id: "task-create-linkedin-post",
            description: "Create LinkedIn post"
          },
          {
            id: "task-create-reddit-teaser",
            description: "Create Reddit teaser"
          },
          {
            id: "task-create-x-thread",
            description: "Create X thread"
          },
          {
            id: "task-build-launch-variation",
            description: "Build a launch variation of your homepage or LP with social proof (Quick win: duplicate your existing page; add Product Hunt banner + top 2–3 testimonials, no redesign needed)"
          },
          {
            id: "task-record-teaser-video",
            description: "Record a teaser or demo video (Tella, ScreenStudio, etc.)"
          },
          {
            id: "task-collect-testimonials",
            description: "Collect 3–5 social proof quotes or testimonials for launch posts"
          }
        ],
        tips: [
          {
            id: "tip-segmentation-prompt-assets",
            content: "Segmentation Prompt\nB2B → ROI visuals, testimonial breakdowns\nDev tools → GitHub stars, performance demos\nCreator tools → Teaser video with \"here's what I made\" vibe"
          }
        ]
      },
      // Phase 36: Launch Execution
      {
        id: "phase-launch-execution",
        number: 36,
        title: "Launch Execution",
        introduction: "This is your spotlight moment. Strong launch engagement leads to more traffic, higher PH ranking, and lasting impressions.",
        keyTargets: [
          "Product Hunt post published before 08:00 PT",
          "Real-time response time ≤ 5 min for every PH comment",
          "Log upvotes, ranking, and traffic (aim: 300 upvotes / Top-5 / 1,000 visitors, but not required to advance)"
        ],
        tasks: [
          {
            id: "task-publish-on-ph",
            description: "Publish on Product Hunt"
          },
          {
            id: "task-announce-across-platforms",
            description: "Announce on X.com, Reddit, and your email list"
          },
          {
            id: "task-engage-with-comments",
            description: "Engage with every comment, mention, and supporter"
          },
          {
            id: "task-share-real-time-updates",
            description: "Share real-time updates (\"We're top 3,\" \"300 upvotes,\" \"Live demo happening now\")"
          }
        ]
      },
      // Phase 37: Viral Loop Engine
      {
        id: "phase-viral-loop-engine",
        number: 37,
        title: "Viral Loop Engine",
        introduction: "A launch without a viral loop is a spike. A launch with sharing mechanics becomes a flywheel.",
        keyTargets: [
          "Referral/share page live and linked from onboarding + email",
          "Viral-loop metrics dashboard created (invites sent, conversion rate)"
        ],
        tasks: [
          {
            id: "task-build-referral-page",
            description: "Build a referral/share page and link it from onboarding email + in-app banner (Fast path: use SparkLoop, Viral Loops, or a simple Tally form + Zapier to generate invite links)"
          },
          {
            id: "task-promote-onboarding",
            description: "Promote this inside onboarding, thank-you pages, and via email"
          },
          {
            id: "task-post-viral-message",
            description: "Post a viral-friendly message on X.com (e.g. \"Invite friends, unlock free month\")"
          },
          {
            id: "task-log-invites",
            description: "Log invites and referral sign-ups (goal: 100 invites / 10 sign-ups, but not required to advance)"
          }
        ],
        tips: [
          {
            id: "tip-segmentation-prompt-viral",
            content: "Segmentation Prompt\nB2B → \"Refer a teammate, get 30% off\"\nDev → GitHub badge or leaderboard\nCreators → Unlock tools, templates, or shoutouts"
          }
        ]
      },
      // Phase 38: Post-Launch Distribution
      {
        id: "phase-post-launch-distribution",
        number: 38,
        title: "Post-Launch Distribution",
        introduction: "Most launches fade fast. This phase turns the buzz into content assets, proof, and conversions that last.",
        keyTargets: [
          "Publish a transparent launch-recap blog post within 72 h",
          "Schedule 3 evergreen assets (carousel, tweet thread, LinkedIn post)",
          "Add the Product Hunt badge and testimonial screenshots to your landing page"
        ],
        tasks: [
          {
            id: "task-write-recap-post",
            description: "Write a transparent launch recap blog post"
          },
          {
            id: "task-create-evergreen-content",
            description: "Turn best launch content into evergreen posts or visuals"
          },
          {
            id: "task-add-ph-badge",
            description: "Add PH badge, testimonial screenshots, or upvote quotes to your LP"
          },
          {
            id: "task-use-highlights",
            description: "Use highlights in onboarding, follow-ups, and future sales pages"
          }
        ]
      }
    ]
  }
]; 